// Marketing Director — Setup: readiness checklist, onboarding interview →
// synthesized positioning/ICP with provenance, channel registration, and
// suppression list. Every wizard step shows its "why we're asking" line.
import { useCallback, useEffect, useState } from "react";
import { marketingFetch } from "../lib/api";

const CHANNELS = [
  [
    "email_graph",
    "Email (Microsoft 365)",
    "Sends go through YOUR tenancy and domain — never a shared mailbox.",
  ],
  [
    "sms_p2p",
    "SMS (your phone)",
    "Every text is sent by you, from your phone, one tap at a time — and only to people who've opted in.",
  ],
  [
    "linkedin",
    "LinkedIn",
    "Draft + manual post only. We never post for you or automate your account.",
  ],
  ["x", "X", "Draft + manual post only."],
  ["facebook", "Facebook", "Draft + manual post only."],
  [
    "instagram",
    "Instagram",
    "Caption + visual brief drafts; you post manually.",
  ],
];

const CHECK_LABELS = {
  positioning_approved: "Positioning doc approved",
  icp_approved: "Ideal customer profile approved",
  channel_connected: "At least one channel connected",
  send_windows_set: "Send windows configured",
};

export function MarketingSetup({ facilityId }) {
  const [readiness, setReadiness] = useState(null);
  const [steps, setSteps] = useState([]);
  const [session, setSession] = useState(null);
  const [answers, setAnswers] = useState({});
  const [artifacts, setArtifacts] = useState([]);
  const [credentials, setCredentials] = useState([]);
  const [suppression, setSuppression] = useState([]);
  const [suppressDraft, setSuppressDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    marketingFetch(`/onboarding/readiness?facility_id=${facilityId}`).then(
      (r) => r.ok && setReadiness(r.data),
    );
    marketingFetch(`/artifacts?facility_id=${facilityId}`).then(
      (r) => r.ok && setArtifacts(r.data.artifacts || []),
    );
    marketingFetch(`/suppression?facility_id=${facilityId}`).then(
      (r) => r.ok && setSuppression(r.data.entries || []),
    );
  }, [facilityId]);
  useEffect(load, [load]);
  useEffect(() => {
    marketingFetch(`/wizard/onboarding/steps`).then(
      (r) => r.ok && setSteps(r.data.steps || []),
    );
  }, []);

  const openWizard = async () => {
    const r = await marketingFetch(`/wizard/sessions`, {
      method: "POST",
      body: JSON.stringify({ facility_id: facilityId, wizard: "onboarding" }),
    });
    if (r.ok) {
      setSession(r.data);
      setAnswers(r.data.state?.answers || {});
    }
  };

  const saveProgress = async (nextAnswers) => {
    setAnswers(nextAnswers);
    if (session) {
      marketingFetch(`/wizard/sessions/${session.id}`, {
        method: "PATCH",
        body: JSON.stringify({
          facility_id: facilityId,
          state: { answers: nextAnswers },
        }),
      });
    }
  };

  const synthesize = async () => {
    setBusy(true);
    setError(null);
    const r = await marketingFetch(
      `/wizard/sessions/${session.id}/synthesize`,
      {
        method: "POST",
        body: JSON.stringify({ facility_id: facilityId }),
      },
    );
    setBusy(false);
    if (!r.ok) {
      const d = r.data?.detail;
      setError(
        d?.issues
          ? d.issues.join("; ")
          : typeof d === "string"
            ? d
            : "Synthesis failed",
      );
      return;
    }
    load();
  };

  const approveArtifact = async (id) => {
    const r = await marketingFetch(`/artifacts/${id}/approve`, {
      method: "POST",
      body: JSON.stringify({ facility_id: facilityId }),
    });
    if (!r.ok) setError("Approval failed (requires the approver role).");
    load();
  };

  const addSuppression = async () => {
    const value = suppressDraft.trim();
    if (!value) return;
    const isDomain = !value.includes("@");
    await marketingFetch(`/suppression`, {
      method: "POST",
      body: JSON.stringify({
        facility_id: facilityId,
        [isDomain ? "domain" : "email"]: value,
        reason: "manual_dnc",
      }),
    });
    setSuppressDraft("");
    load();
  };

  const latestByType = (type) =>
    artifacts
      .filter((a) => a.artifact_type === type)
      .sort((a, b) => b.version - a.version);

  return (
    <div className="space-y-6">
      {error && (
        <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
          {error}
          <button className="ml-3 underline" onClick={() => setError(null)}>
            dismiss
          </button>
        </div>
      )}

      {readiness && (
        <div className="p-4 rounded-xl border border-slate-200 bg-white">
          <div className="font-semibold text-slate-700 mb-2">
            Readiness checklist
            {readiness.ready ? (
              <span className="ml-2 text-red-700">
                — ready, discovery and campaigns are unlocked
              </span>
            ) : (
              <span className="ml-2 text-slate-500">
                — complete these to unlock discovery and campaigns
              </span>
            )}
          </div>
          <ul className="text-sm space-y-1">
            {Object.entries(readiness.checks).map(([k, v]) => (
              <li key={k} className={v ? "text-slate-600" : "text-slate-400"}>
                {v ? "✓" : "○"} {CHECK_LABELS[k] || k}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="p-4 rounded-xl border border-slate-200 bg-white space-y-3">
        <div className="flex justify-between items-center">
          <div className="font-semibold text-slate-700">Business interview</div>
          {!session && (
            <button
              onClick={openWizard}
              className="px-3 py-1.5 rounded-lg bg-red-700 text-white text-sm hover:bg-red-800"
            >
              {steps.length && artifacts.length ? "Revisit interview" : "Start"}
            </button>
          )}
        </div>
        {session && (
          <>
            {session.resumed && (
              <div className="text-xs text-slate-500">
                Resumed where you left off.
              </div>
            )}
            {steps.map((s) => (
              <label key={s.key} className="block text-sm">
                <span className="text-slate-700 font-medium">{s.question}</span>
                <span className="block text-xs text-slate-400 mb-1">
                  Why we ask: {s.why}
                </span>
                <textarea
                  rows={2}
                  value={answers[s.key] || ""}
                  onChange={(e) =>
                    saveProgress({ ...answers, [s.key]: e.target.value })
                  }
                  className="w-full px-3 py-2 rounded-lg border border-slate-300"
                />
              </label>
            ))}
            <button
              disabled={busy || !Object.values(answers).some((v) => v?.trim())}
              onClick={synthesize}
              className="px-4 py-2 rounded-lg bg-red-700 text-white text-sm disabled:opacity-50"
            >
              {busy ? "Synthesizing…" : "Synthesize positioning + ICP"}
            </button>
          </>
        )}
      </div>

      {["positioning", "icp"].map((type) => {
        const versions = latestByType(type);
        if (!versions.length) return null;
        const latest = versions[0];
        return (
          <div
            key={type}
            className="p-4 rounded-xl border border-slate-200 bg-white space-y-2"
          >
            <div className="flex justify-between items-center">
              <div className="font-semibold text-slate-700">
                {type === "positioning"
                  ? "Positioning doc"
                  : "Ideal customer profile"}
                <span className="ml-2 text-sm text-slate-500">
                  v{latest.version} · {latest.status}
                </span>
              </div>
              {latest.status === "draft" && (
                <button
                  onClick={() => approveArtifact(latest.id)}
                  className="px-3 py-1 rounded bg-red-700 text-white text-xs"
                >
                  Approve
                </button>
              )}
            </div>
            <pre className="text-sm text-slate-600 whitespace-pre-wrap font-sans">
              {type === "positioning"
                ? latest.content.markdown
                : JSON.stringify(latest.content, null, 2)}
            </pre>
            {(latest.provenance || []).length > 0 && (
              <div className="text-xs text-slate-400 space-y-0.5">
                {latest.provenance.map((a, i) => (
                  <div key={i}>
                    ↳ {a.note || `${a.element} ← ${a.from_answer}`}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}

      <div className="p-4 rounded-xl border border-slate-200 bg-white">
        <div className="font-semibold text-slate-700 mb-2">Channels</div>
        <div className="text-xs text-slate-400 mb-3">
          A channel exists for your account only once it's connected here. There
          is no shared or platform-owned sender for any channel.
        </div>
        <ChannelRows
          facilityId={facilityId}
          credentials={credentials}
          setCredentials={setCredentials}
          onChanged={load}
        />
      </div>

      <SendWindowsCard facilityId={facilityId} onChanged={load} />

      <div className="p-4 rounded-xl border border-slate-200 bg-white space-y-2">
        <div className="font-semibold text-slate-700">Do-not-contact list</div>
        <div className="text-xs text-slate-400">
          Addresses are stored as hashes only. Paste an email or a whole domain.
        </div>
        <div className="flex gap-2">
          <input
            value={suppressDraft}
            onChange={(e) => setSuppressDraft(e.target.value)}
            placeholder="name@example.com or example.com"
            className="flex-1 px-3 py-2 rounded-lg border border-slate-300 text-sm"
          />
          <button
            onClick={addSuppression}
            className="px-3 py-2 rounded-lg border border-slate-300 text-sm text-slate-700"
          >
            Add
          </button>
        </div>
        <div className="text-sm text-slate-500">
          {suppression.length} entries.
        </div>
      </div>
    </div>
  );
}

const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"]; // ISO 1–7

function SendWindowsCard({ facilityId, onChanged }) {
  const [rule, setRule] = useState(null); // facility-wide default rule, if any
  const [days, setDays] = useState([1, 2, 3, 4, 5]);
  const [hoursStart, setHoursStart] = useState(9);
  const [hoursEnd, setHoursEnd] = useState(17);
  const [timezone, setTimezone] = useState("America/Phoenix");
  const [dailyCap, setDailyCap] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    marketingFetch(`/send-windows?facility_id=${facilityId}`).then((r) => {
      if (!r.ok) return;
      const dflt = (r.data.rules || []).find(
        (x) => !x.channel && !x.segment_key,
      );
      if (dflt) {
        setRule(dflt);
        setDays(dflt.allowed_days || [1, 2, 3, 4, 5]);
        setHoursStart(dflt.allowed_hours_start);
        setHoursEnd(dflt.allowed_hours_end);
        setTimezone(dflt.timezone);
        setDailyCap(dflt.daily_cap == null ? "" : String(dflt.daily_cap));
      }
    });
  }, [facilityId]);

  const toggleDay = (d) =>
    setDays(days.includes(d) ? days.filter((x) => x !== d) : [...days, d]);

  const save = async () => {
    setSaving(true);
    setError(null);
    const r = await marketingFetch(`/send-windows`, {
      method: "PUT",
      body: JSON.stringify({
        facility_id: facilityId,
        allowed_days: days,
        allowed_hours_start: hoursStart,
        allowed_hours_end: hoursEnd,
        timezone,
        daily_cap: dailyCap.trim() ? Number(dailyCap) : null,
      }),
    });
    setSaving(false);
    if (!r.ok) {
      const d = r.data?.detail;
      setError(typeof d === "string" ? d : "Save failed");
      return;
    }
    setRule(r.data.rule);
    onChanged();
  };

  return (
    <div className="p-4 rounded-xl border border-slate-200 bg-white space-y-3">
      <div className="font-semibold text-slate-700">
        Send windows
        {rule && (
          <span className="ml-2 text-xs text-red-700">● configured</span>
        )}
      </div>
      <div className="text-xs text-slate-400">
        Outreach only goes out on these days and hours (in your timezone).
        Applies to every channel unless a channel-specific rule exists.
      </div>
      {error && <div className="text-sm text-red-700">{error}</div>}
      <div className="flex flex-wrap gap-1.5">
        {DAY_LABELS.map((label, i) => {
          const d = i + 1;
          const on = days.includes(d);
          return (
            <button
              key={d}
              onClick={() => toggleDay(d)}
              className={`px-2.5 py-1 rounded-lg border text-xs ${
                on
                  ? "bg-red-700 border-red-700 text-white"
                  : "border-slate-300 text-slate-500"
              }`}
            >
              {label}
            </button>
          );
        })}
      </div>
      <div className="flex flex-wrap items-center gap-3 text-sm">
        <label className="flex items-center gap-1.5 text-slate-600">
          From
          <select
            value={hoursStart}
            onChange={(e) => setHoursStart(Number(e.target.value))}
            className="px-2 py-1.5 rounded-lg border border-slate-300"
          >
            {Array.from({ length: 24 }, (_, h) => (
              <option key={h} value={h}>
                {String(h).padStart(2, "0")}:00
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-1.5 text-slate-600">
          to
          <select
            value={hoursEnd}
            onChange={(e) => setHoursEnd(Number(e.target.value))}
            className="px-2 py-1.5 rounded-lg border border-slate-300"
          >
            {Array.from({ length: 24 }, (_, h) => (
              <option key={h} value={h}>
                {String(h).padStart(2, "0")}:00
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-1.5 text-slate-600">
          Timezone
          <input
            value={timezone}
            onChange={(e) => setTimezone(e.target.value)}
            placeholder="America/Phoenix"
            className="px-2 py-1.5 rounded-lg border border-slate-300 w-44"
          />
        </label>
        <label className="flex items-center gap-1.5 text-slate-600">
          Daily cap
          <input
            value={dailyCap}
            onChange={(e) => setDailyCap(e.target.value.replace(/\D/g, ""))}
            placeholder="none"
            className="px-2 py-1.5 rounded-lg border border-slate-300 w-20"
          />
        </label>
      </div>
      <button
        disabled={saving || !days.length || hoursStart >= hoursEnd}
        onClick={save}
        className="px-4 py-2 rounded-lg bg-red-700 text-white text-sm disabled:opacity-50"
      >
        {saving ? "Saving…" : rule ? "Update send windows" : "Save send windows"}
      </button>
    </div>
  );
}

function ChannelRows({ facilityId, credentials, setCredentials, onChanged }) {
  const [drafts, setDrafts] = useState({});
  const load = useCallback(() => {
    marketingFetch(`/channels?facility_id=${facilityId}`).then(
      (r) => r.ok && setCredentials(r.data.channels || []),
    );
  }, [facilityId, setCredentials]);
  useEffect(load, [load]);
  const byChannel = Object.fromEntries(
    (credentials || []).map((c) => [c.channel, c]),
  );

  return (
    <div className="space-y-2">
      {CHANNELS.map(([key, label, desc]) => (
        <div
          key={key}
          className="flex flex-wrap items-center gap-2 text-sm border-t border-slate-100 pt-2"
        >
          <div className="w-56 text-slate-700 font-medium">
            {label}
            {byChannel[key]?.status === "connected" && (
              <span className="ml-2 text-xs text-red-700">
                ● {byChannel[key].identity_label}
              </span>
            )}
          </div>
          <div className="flex-1 text-xs text-slate-400 min-w-[220px]">
            {desc}
          </div>
          <input
            value={drafts[key] || ""}
            onChange={(e) => setDrafts({ ...drafts, [key]: e.target.value })}
            placeholder={
              key === "email_graph"
                ? "mailbox address"
                : key === "sms_p2p"
                  ? "your phone number"
                  : "handle / page name"
            }
            className="px-3 py-1.5 rounded-lg border border-slate-300 text-sm"
          />
          <button
            onClick={async () => {
              await marketingFetch(`/channels/register`, {
                method: "POST",
                body: JSON.stringify({
                  facility_id: facilityId,
                  channel: key,
                  identity_label: drafts[key] || "",
                }),
              }).then((r) => {
                if (!r.ok)
                  window.alert(
                    "Registration failed — is the identity filled in?",
                  );
              });
              load();
              onChanged();
            }}
            className="px-3 py-1.5 rounded-lg border border-slate-300 text-slate-700 text-sm"
          >
            Register
          </button>
        </div>
      ))}
    </div>
  );
}
